// assets/vendors-a-z.js — strict include + robust A–Z bucketing

document.addEventListener('DOMContentLoaded', () => {
  console.log('[brands] A–Z directory (strict include)');

  // --- API config ---
  const API_URL = '/api/2024-07/graphql.json';
  const TOKEN   = '720c9a5c983ccd433579c7ba4b581e1d'; // keep private in repos

  // --- DOM hooks (unchanged) ---
  const sectionsEl  = document.getElementById('vendor-sections');
  const letterLinks = Array.from(document.querySelectorAll('.letter-link'));
  if (!sectionsEl || !letterLinks.length) return;

  // --- Filter config ---
  const USE_METAFIELD = true;
  const META_NS = 'custom';
  const META_KEY = 'is_brand';

  // --- Helpers ---
  const fitWidth = (url, w = 900) =>
    (!url ? '' : url + (url.includes('?') ? `&width=${w}` : `?width=${w}`));
  const collectionUrl = (h) => '/collections/' + h;
  const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  // Find the FIRST A–Z anywhere in the title after removing zero-width & accents.
  function bucketLetter(title){
    const norm = (title || '')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')   // zero-width chars/BOM
      .normalize('NFD')                        // split accents (Á -> A + ́)
      .replace(/[\u0300-\u036f]/g, '')        // strip diacritics
      .toUpperCase();
    const m = norm.match(/[A-Z]/);
    return m ? m[0] : null;
  }

  // --- Fetch collections (STRICT include: only when is_brand is explicitly truthy) ---
  async function fetchBrandCollections(){
    const q = `
      query($cursor:String){
        collections(first:250, after:$cursor){
          edges{
            cursor
            node{
              handle
              title
              description
              image { url altText }
              metafield(namespace:"${META_NS}", key:"${META_KEY}") { value }
            }
          }
          pageInfo { hasNextPage endCursor }
        }
      }`;

    let cursor = null, tries = 0;
    const out = [];

    while (tries++ < 100){
      const res = await fetch(API_URL, {
        method:'POST',
        headers:{
          'Content-Type':'application/json',
          'X-Shopify-Storefront-Access-Token': TOKEN
        },
        body: JSON.stringify({ query:q, variables:{ cursor } })
      });
      if (!res.ok) throw new Error('Storefront API ' + res.status);

      const json  = await res.json();
      const edges = json?.data?.collections?.edges || [];

      edges.forEach(({ node }) => {
        if (USE_METAFIELD) {
          const raw = node?.metafield?.value;
          const v = (raw === true ? 'true' : String(raw ?? '')).trim().toLowerCase();
          const truthy = ['true','1','yes','on','y','t'].includes(v);

          if (truthy) {
            out.push(node);      // STRICT: include only when explicitly true-like
          } else {
            // missing/null/false/empty => exclude
          }
        } else {
          out.push(node);        // only used if you disable the metafield filter entirely
        }
      });

      const pi = json?.data?.collections?.pageInfo;
      if (pi?.hasNextPage) cursor = pi.endCursor; else break;
    }

    // sort by title (case-insensitive)
    out.sort((a,b)=>(a.title||'').toLowerCase().localeCompare((b.title||'').toLowerCase()));
    return out;
  }

  // --- Render (kept same structure you had) ---
  let byLetter = {};

  function buildSections(collections){
    const alphabet = ALPHABET.slice();      // A–Z only
    byLetter = {}; alphabet.forEach(L => byLetter[L] = []);

    collections.forEach(c => {
      const t = (c.title || '').trim();
      if (!t) return;
      const L = bucketLetter(t);
      if (L && ALPHABET.includes(L)) {
        byLetter[L].push(c);
      }
    });

    sectionsEl.innerHTML = alphabet.map(L => {
      const list = byLetter[L] || [];
      if (!list.length) return '';

      // Letter tile (first grid cell)
      const letterTile = `
        <div class="letter-tile" style="grid-column:span 1" role="heading" aria-level="2" aria-label="Brands starting with ${L}">
          <span class="letter-lg">${L}</span>
        </div>`;

      const cards = list.map(c=>{
        const url   = c?.image?.url ? fitWidth(c.image.url, 900) : '';
        const alt   = c?.image?.altText || c.title || '';
        const desc  = (c?.description || '').trim();
        const blurb = desc ? (desc.length > 220 ? desc.slice(0,217)+'…' : desc) : '';

        return `
          <div class="brand-card" style="grid-column:span 1">
            <a href="${collectionUrl(c.handle)}" aria-label="${alt}">
              <div class="brand-image" style="${url ? `background-image:url('${url.replace(/'/g,"\\'")}')` : ''}"></div>
              <div class="brand-info">
                <h3 class="brand-title">${c.title || ''}</h3>
                ${blurb ? `<p class="brand-blurb">${blurb}</p>` : ``}
              </div>
            </a>
          </div>`;
      }).join('');

      return `
        <section id="letter-${L}" class="letter-section" data-letter="${L}" style="display:none">
          <div class="brand-grid">${letterTile}${cards}</div>
        </section>`;
    }).join('');

    // Hide A–Z pills with no data
    letterLinks.forEach(a=>{
      const L = a.textContent.trim().toUpperCase();
      a.style.display = (byLetter[L] && byLetter[L].length) ? '' : 'none';
    });
  }

  // No scrolling here — just show/hide relevant sections & update pills
  function showLetter(L){
    document.querySelectorAll('.letter-section').forEach(sec=>{
      sec.style.display = (sec.dataset.letter === L) ? '' : 'none';
    });
    letterLinks.forEach(a =>
      a.classList.toggle('active', a.textContent.trim().toUpperCase() === L)
    );
  }

  // Letter pill clicks: just change letter, no scroll
  letterLinks.forEach(a=>{
    a.addEventListener('click', e=>{
      e.preventDefault();
      const L = a.textContent.trim().toUpperCase();
      showLetter(L);
    });
  });

  fetchBrandCollections()
    .then(cols=>{
      buildSections(cols);

      const qLetter = (new URLSearchParams(location.search).get('letter')||'').toUpperCase();
      const firstWithData = letterLinks
        .map(a=>a.textContent.trim().toUpperCase())
        .find(L => (byLetter[L]||[]).length);

      // Initial state: show query letter if valid, else first populated letter, else A
      showLetter(
        qLetter && (byLetter[qLetter]||[]).length ? qLetter : (firstWithData || 'A')
      );
    })
    .catch(err=>{
      console.error('[brands] failed', err);
      sectionsEl.innerHTML = '<p style="text-align:center">Unable to load brands.</p>';
    });
});
